import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config/config';
import { TokenService } from './tokenService';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  members: T[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

const api = axios.create({
  baseURL: config.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add timeout to prevent hanging requests
  timeout: 10000,
  // Ensure complete response
  maxContentLength: 10 * 1024 * 1024, // 10MB
  validateStatus: (status) => status >= 200 && status < 300,
});

// Retry helper function
const retryRequest = async (fn: () => Promise<any>, retries: number = MAX_RETRIES): Promise<any> => {
  try {
    return await fn();
  } catch (error: any) {
    if (retries > 0 && (
      error.code === 'ERR_INCOMPLETE_CHUNKED_ENCODING' ||
      error.code === 'ECONNABORTED' ||
      (error.response?.status >= 500 && error.response?.status < 600)
    )) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

// Add request interceptor
api.interceptors.request.use(
  (config) => {
    // Add Authorization header if token exists
    const authHeader = TokenService.getAuthHeader();
    if (authHeader) {
      config.headers['Authorization'] = authHeader;
    }
    
    // Remove Content-Type for FormData (browser will set it automatically with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh function
const refreshToken = async (): Promise<boolean> => {
  const refreshToken = TokenService.getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await axios.post(`${config.API_BASE_URL}/api/auth/refresh`, {
      refreshToken: refreshToken
    });

    if (response.data.success && response.data.data) {
      const tokenData = response.data.data;
      TokenService.updateAccessToken(tokenData.access_token, tokenData.expires_in);
      return true;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    TokenService.clearTokens();
  }

  return false;
};

let pendingRefresh: Promise<boolean> | null = null;
type SessionRequest = InternalAxiosRequestConfig & { sessionRetried?: boolean };

// Add response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as SessionRequest | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest.url?.includes('/api/auth/')) {
      // Try to refresh token
      let refreshed = false;
      if (!originalRequest.sessionRetried) {
        originalRequest.sessionRetried = true;
        if (!pendingRefresh) pendingRefresh = refreshToken().finally(() => { pendingRefresh = null; });
        refreshed = await pendingRefresh;
      }
      
      if (refreshed && originalRequest) {
        // Retry the original request with new token
        const authHeader = TokenService.getAuthHeader();
        if (authHeader) {
          originalRequest.headers['Authorization'] = authHeader;
        }
        return api(originalRequest);
      } else {
        // Refresh failed, redirect to login
        TokenService.clearTokens();
        window.location.href = '/login';
      }
    }

    if (error.response?.status === 403) {
      // Don't redirect on auth endpoints to prevent loops
      if (!error.config?.url?.includes('/api/auth/')) {
        TokenService.clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
      }
    }

    return Promise.reject(error);
  }
);

export const apiService = {
  // External messages must not be automatically retried after a timeout or 5xx.
  async postOnce<T>(url:string,data:unknown,fallbackMessage='Sending could not be confirmed. Check SMS gateway history before trying again.'):Promise<ApiResponse<T>> {
    try {
      const response=await api.post(url,data,{timeout:45000});
      return {success:response.data.success??true,data:response.data.data??response.data,message:response.data.message};
    } catch (error:any) {
      return {success:false,message:error.response?.data?.message || error.response?.data?.detail || fallbackMessage};
    }
  },
  async get<T>(url: string): Promise<ApiResponse<T>> {
    return retryRequest(async () => {
      try {
        const response = await api.get(url);
        console.log('API Response:', { url, response });
        
        // Handle direct data response (no success/data wrapper)
        if (response.data && typeof response.data.success === 'undefined') {
          return {
            success: true,
            data: response.data
          };
        }
        
        // Handle wrapped response
        if (!response.data) {
          throw new Error('Empty response received');
        }
        
        return {
          success: response.data.success ?? true,
          data: response.data.data ?? response.data,
          message: response.data.message
        };
      } catch (error: any) {
        // Enhanced error handling
        const errorMessage = error.response?.data?.message 
          || error.message 
          || 'An error occurred while fetching data';
        
        console.error(`API Error (GET ${url}):`, {
          message: errorMessage,
          code: error.code,
          status: error.response?.status,
          error
        });

        return {
          success: false,
          message: errorMessage
        };
      }
    });
  },

  async post<T>(url: string, data: any): Promise<ApiResponse<T>> {
    return retryRequest(async () => {
      try {
        const response = await api.post(url, data);
        console.log('API Response:', { url, response });
        
        // Handle direct data response (no success/data wrapper)
        if (response.data && typeof response.data.success === 'undefined') {
          return {
            success: true,
            data: response.data
          };
        }
        
        // Handle wrapped response
        if (!response.data) {
          throw new Error('Empty response received');
        }
        
        return {
          success: response.data.success ?? true,
          data: response.data.data ?? response.data,
          message: response.data.message
        };
      } catch (error: any) {
        const errorMessage = error.response?.data?.message 
          || error.message 
          || 'An error occurred while posting data';
        
        console.error(`API Error (POST ${url}):`, {
          message: errorMessage,
          code: error.code,
          status: error.response?.status,
          error
        });

        return {
          success: false,
          message: errorMessage
        };
      }
    });
  },

  async put<T>(url: string, data: any): Promise<ApiResponse<T>> {
    return retryRequest(async () => {
      try {
        const response = await api.put(url, data);
        console.log('API Response:', { url, response });
        
        // Handle direct data response (no success/data wrapper)
        if (response.data && typeof response.data.success === 'undefined') {
          return {
            success: true,
            data: response.data
          };
        }
        
        // Handle wrapped response
        if (!response.data) {
          throw new Error('Empty response received');
        }
        
        return {
          success: response.data.success ?? true,
          data: response.data.data ?? response.data,
          message: response.data.message
        };
      } catch (error: any) {
        const errorMessage = error.response?.data?.message 
          || error.message 
          || 'An error occurred while updating data';
        
        console.error(`API Error (PUT ${url}):`, {
          message: errorMessage,
          code: error.code,
          status: error.response?.status,
          error
        });

        return {
          success: false,
          message: errorMessage
        };
      }
    });
  },

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return retryRequest(async () => {
      try {
        const response = await api.delete(url);
        console.log('API Response:', { url, response });
        
        // Handle direct data response (no success/data wrapper)
        if (response.data && typeof response.data.success === 'undefined') {
          return {
            success: true,
            data: response.data
          };
        }
        
        // Handle wrapped response
        if (!response.data) {
          throw new Error('Empty response received');
        }
        
        return {
          success: response.data.success ?? true,
          data: response.data.data ?? response.data,
          message: response.data.message
        };
      } catch (error: any) {
        const errorMessage = error.response?.data?.message 
          || error.message 
          || 'An error occurred while deleting data';
        
        console.error(`API Error (DELETE ${url}):`, {
          message: errorMessage,
          code: error.code,
          status: error.response?.status,
          error
        });

        return {
          success: false,
          message: errorMessage
        };
      }
    });
  }
};
